import { Box, Typography } from '@mui/material';
import { ReactNode, useState } from 'react';

import { useUpdatePiecesMutation } from '@api/piece';
import { PieceAction } from '@apiContract';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';
import { showApiErrorToast } from '@utils/apiErrorToast';
import LabelSwitch from 'src/components/Inputs/LabelSwitch';

import { ActionComponentProps } from '../../index';
import { CollapsibleOptions } from './CollapsibleOptions';
import { buildUpdatePiecesRequest } from './helper';

type RequestActionType =
  | PieceAction.RequestDelivery
  | PieceAction.RequestOpenAndScan
  | PieceAction.RequestRecycle
  | PieceAction.RequestReturnToSender
  | PieceAction.RequestShred;

type RequestActionConfig = {
  title: { single: string; plural: string };
  question: { single: string; plural: (count: number) => ReactNode };
};

const REQUEST_ACTION_CONFIG: Record<RequestActionType, RequestActionConfig> = {
  [PieceAction.RequestDelivery]: {
    title: { single: 'Request delivery', plural: 'Request delivery for selected items' },
    question: {
      single: 'Are you sure you would like this item to be delivered?',
      plural: (count) => (
        <>
          Are you sure you would like <strong>{count}</strong> items to be delivered?
        </>
      ),
    },
  },
  [PieceAction.RequestOpenAndScan]: {
    title: { single: 'Request open & scan', plural: 'Request open & scan for selected items' },
    question: {
      single: 'Are you sure you would like this item to be opened and scanned?',
      plural: (count) => (
        <>
          Are you sure you would like <strong>{count}</strong> items to be opened and scanned?
        </>
      ),
    },
  },
  [PieceAction.RequestRecycle]: {
    title: { single: 'Request recycle', plural: 'Request recycle for selected items' },
    question: {
      single: 'Are you sure you would like this item to be recycled?',
      plural: (count) => (
        <>
          Are you sure you would like <strong>{count}</strong> items to be recycled?
        </>
      ),
    },
  },
  [PieceAction.RequestReturnToSender]: {
    title: { single: 'Request return to sender', plural: 'Request return to sender for selected items' },
    question: {
      single: 'Are you sure you would like this item to be returned to its sender?',
      plural: (count) => (
        <>
          Are you sure you would like <strong>{count}</strong> items to be returned to their senders?
        </>
      ),
    },
  },
  [PieceAction.RequestShred]: {
    title: { single: 'Request shred', plural: 'Request shred for selected items' },
    question: {
      single: 'Are you sure you would like this item to be shredded?',
      plural: (count) => (
        <>
          Are you sure you would like <strong>{count}</strong> items to be shredded?
        </>
      ),
    },
  },
};

type RequestActionDialogProps = ActionComponentProps & {
  action: RequestActionType;
};

export const RequestActionDialog = ({
  mailpieces,
  actionStarted,
  onActionFinish,
  isForce,
  action,
}: RequestActionDialogProps) => {
  const [updatePieces, { isLoading }] = useUpdatePiecesMutation();
  const [shouldNotifyMailroom, setShouldNotifyMailroom] = useState(false);

  const { title, question } = REQUEST_ACTION_CONFIG[action];
  const isSingle = mailpieces.length === 1;

  const confirmAction = async () => {
    const requestData = buildUpdatePiecesRequest({
      mailpieces,
      action,
      isForce,
      shouldNotifyMailroom,
    });

    try {
      await updatePieces(requestData).unwrap();

      onActionFinish();
    } catch (error) {
      showApiErrorToast(error);
    }
  };

  const content = (
    <Box>
      <Typography variant="body1" color="text.secondary">
        {isSingle ? question.single : question.plural(mailpieces.length)}
      </Typography>

      {isForce && (
        <CollapsibleOptions>
          <Box sx={{ pt: 1 }}>
            <LabelSwitch
              checked={shouldNotifyMailroom}
              onChange={() => setShouldNotifyMailroom(!shouldNotifyMailroom)}
              label="Notify mailroom users about pending request"
            />
          </Box>
        </CollapsibleOptions>
      )}
    </Box>
  );

  return (
    <DialogConfirmation
      open={actionStarted}
      loading={isLoading}
      maxWidth="xs"
      title={isSingle ? title.single : title.plural}
      content={content}
      acceptButtonHandleClick={confirmAction}
      cancelButtonHandleClick={onActionFinish}
      acceptButtonTitle="Submit"
      cancelButtonTitle="Cancel"
    />
  );
};
